import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot } from "firebase/firestore";
import {
  CHECKLIST_STAGES,
  COL_CHECKLISTS,
  COL_PIPELINE_DEALS,
  type Checklist,
  type PipelineDeal,
} from "@closing/shared";
import { db } from "../lib/firebase";
import { api } from "../lib/api";

type DealRow = PipelineDeal & { id: string };
type View = "active" | "closed";

// Stage pill colors — identical to Deal Pipeline's STAGE_COLORS so the two
// tools read the same at a glance.
const STAGE_COLORS: Record<string, { bg: string; text: string }> = {
  "Post-Closing": { bg: "#bf9000", text: "#fff" },
  Legal: { bg: "#93c47d", text: "#000" },
  "Term Sheet": { bg: "#9fc5e8", text: "#000" },
  "Screen Ready": { bg: "#e69138", text: "#fff" },
  "Pre-Screen": { bg: "#c27ba0", text: "#fff" },
  "Post-Call": { bg: "#ffe599", text: "#000" },
  "Pre-Call": { bg: "#b4a7d6", text: "#000" },
};

export default function Deals() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState<DealRow[]>([]);
  const [checklists, setChecklists] = useState<Record<string, Checklist>>({});
  const [search, setSearch] = useState("");
  const [view, setView] = useState<View>("active");
  const [creating, setCreating] = useState<string | null>(null);

  useEffect(() => {
    const unsubDeals = onSnapshot(collection(db, COL_PIPELINE_DEALS), (snap) => {
      setDeals(snap.docs.map((d) => ({ id: d.id, ...(d.data() as PipelineDeal) })));
    });
    const unsubChecklists = onSnapshot(collection(db, COL_CHECKLISTS), (snap) => {
      const map: Record<string, Checklist> = {};
      for (const d of snap.docs) map[d.id] = d.data() as Checklist;
      setChecklists(map);
    });
    return () => {
      unsubDeals();
      unsubChecklists();
    };
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return deals
      .filter((d) => {
        // Search reaches every deal regardless of view (edge cases).
        if (q) {
          return (
            d.deal?.toLowerCase().includes(q) || d.company?.toLowerCase().includes(q)
          );
        }
        if (view === "closed") return d.status === "Closed";
        // Active view: deals that already have a checklist (and aren't
        // closed), plus Active deals in a checklist stage needing one.
        if (d.status === "Closed") return false;
        if (checklists[d.id]) return true;
        return (
          d.status === "Active" &&
          !!d.stage &&
          (CHECKLIST_STAGES as readonly string[]).includes(d.stage)
        );
      })
      .sort((a, b) => (a.deal ?? "").localeCompare(b.deal ?? ""));
  }, [deals, checklists, search, view]);

  async function createChecklist(dealId: string) {
    // Creating instantiates the full template and auto-assigns the team —
    // too consequential for a stray tap (especially the mobile cards), so
    // always confirm first. Surface the Research Contact here: a deal
    // without one gets its research-role items silently unassigned (the
    // BURU lesson), so make that visible before creating.
    const deal = deals.find((d) => d.id === dealId);
    const name = deal?.deal ?? dealId;
    const rc = deal?.researchContact?.trim();
    const rcNote = rc
      ? `Research-contact items will be assigned to: ${rc.split(",")[0]!.trim()}.`
      : `⚠️ This deal has NO Research Contact in Deal Pipeline — those items will be left unassigned. Consider setting it first.`;
    if (!window.confirm(`Create the closing checklist for ${name}?\n\n${rcNote}`)) return;
    setCreating(dealId);
    try {
      await api.instantiateChecklist(dealId);
      navigate(`/deals/${encodeURIComponent(dealId)}`);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(null);
    }
  }

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow text-signature">Deals</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">Closing Checklists</h1>
        </div>
        <div className="flex items-center gap-3">
          {/* Active / Closed view tabs */}
          <div className="flex overflow-hidden rounded-md border border-line">
            {(["active", "closed"] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-2 font-mono text-[11px] uppercase tracking-[0.08em] transition-colors ${
                  view === v && !search
                    ? "bg-ink text-white"
                    : "bg-white text-muted hover:text-ink"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search all deals…"
            className="rounded-md border border-line bg-white px-3 py-2 text-sm outline-none focus:border-line-strong"
          />
        </div>
      </div>

      {/* Mobile: cards */}
      <div className="space-y-3 md:hidden">
        {visible.map((d) => {
          const cl = checklists[d.id];
          const items = cl ? Object.values(cl.items) : [];
          const done = items.filter((i) => i.completed).length;
          const pct = items.length ? Math.round((done / items.length) * 100) : 0;
          return (
            <button
              key={d.id}
              onClick={() =>
                cl ? navigate(`/deals/${encodeURIComponent(d.id)}`) : void createChecklist(d.id)
              }
              disabled={creating === d.id}
              className="block w-full rounded-[10px] border border-line bg-white p-4 text-left"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[15px] font-semibold text-ink">
                  {d.deal ?? d.id}
                </span>
                <span
                  className={
                    "badge flex-none" +
                    (d.stage && STAGE_COLORS[d.stage] ? "" : " bg-neutral-bg text-ink-soft")
                  }
                  style={
                    d.stage && STAGE_COLORS[d.stage]
                      ? {
                          backgroundColor: STAGE_COLORS[d.stage]!.bg,
                          color: STAGE_COLORS[d.stage]!.text,
                        }
                      : undefined
                  }
                >
                  {d.stage ?? "—"}
                </span>
              </div>
              {d.company && <p className="mt-0.5 truncate text-[13px] text-muted">{d.company}</p>}
              {cl ? (
                <div className="mt-3 flex items-center gap-3">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-line">
                    <div
                      className={`h-full rounded-full ${pct === 100 ? "bg-positive" : "bg-signature"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="font-mono text-[12px] tabular-nums text-ink-soft">
                    {done}/{items.length}
                  </span>
                </div>
              ) : (
                <p className="mt-3 font-mono text-[11px] font-semibold uppercase tracking-wider text-signature">
                  {creating === d.id ? "Creating…" : "Tap to create checklist"}
                </p>
              )}
            </button>
          );
        })}
        {visible.length === 0 && (
          <p className="py-10 text-center font-mono text-xs text-muted">
            {search ? "No deals match." : view === "closed" ? "No closed deals." : "No active deals in Legal or Post-Closing."}
          </p>
        )}
      </div>

      {/* Desktop: table */}
      <div className="hidden overflow-hidden rounded-[10px] border border-line bg-white md:block">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr>
              <Th>Deal</Th>
              <Th>Company</Th>
              <Th>Stage</Th>
              <Th className="text-right">Checklist</Th>
            </tr>
          </thead>
          <tbody>
            {visible.map((d) => {
              const cl = checklists[d.id];
              const items = cl ? Object.values(cl.items) : [];
              const done = items.filter((i) => i.completed).length;
              return (
                <tr
                  key={d.id}
                  onClick={() => cl && navigate(`/deals/${encodeURIComponent(d.id)}`)}
                  className={`border-b border-line last:border-b-0 hover:bg-paper/60 ${cl ? "cursor-pointer" : ""}`}
                >
                  <td className="px-4 py-2.5 font-mono font-semibold">{d.deal ?? d.id}</td>
                  <td className="px-4 py-2.5 text-ink-soft">{d.company ?? "—"}</td>
                  <td className="px-4 py-2.5">
                    <span
                      className={
                        "badge" +
                        (d.stage && STAGE_COLORS[d.stage] ? "" : " bg-neutral-bg text-ink-soft")
                      }
                      style={
                        d.stage && STAGE_COLORS[d.stage]
                          ? {
                              backgroundColor: STAGE_COLORS[d.stage]!.bg,
                              color: STAGE_COLORS[d.stage]!.text,
                            }
                          : undefined
                      }
                    >
                      {d.stage ?? "—"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums">
                    {cl ? (
                      <span className={done === items.length && items.length > 0 ? "text-positive" : ""}>
                        {done}/{items.length}
                      </span>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          void createChecklist(d.id);
                        }}
                        disabled={creating === d.id}
                        className="rounded-md border border-signature px-2.5 py-1 font-sans text-[11px] font-semibold text-signature transition-colors hover:bg-signature hover:text-white disabled:opacity-50"
                      >
                        {creating === d.id ? "Creating…" : "Create checklist"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center font-mono text-xs text-muted">
                  {search
                    ? "No deals match."
                    : view === "closed"
                      ? "No closed deals."
                      : "No active deals in Legal or Post-Closing."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={
        "border-b border-line-strong px-4 py-2 text-left font-sans text-[10px] font-bold uppercase tracking-[0.08em] text-ink-soft " +
        className
      }
    >
      {children}
    </th>
  );
}
